import React, { useState, useEffect } from "react";
import { API_BASE } from "../config";

export function CollectivitesAdmin({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ type: "", msg: "" });

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast({ type: "", msg: "" }), 3200);
  };

  const limit = 10;

  const fetchCollectivites = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(
        `${API_BASE}/admin/collectivites?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setError(data.msg || "Erreur chargement collectivités");
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch {
      setError("Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollectivites();
  }, [page]);

  const pages = Math.max(1, Math.ceil(total / limit));

  const doToggle = async () => {
    if (!confirm) return;

    setActionLoading(true);

    try {
      const res = await fetch(
        `${API_BASE}/admin/collectivites/${confirm.id}/actif`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ actif: confirm.nextActif }),
        }
      );

      if (!res.ok) throw new Error();

      setItems((prev) =>
        prev.map((c) =>
          c.id === confirm.id ? { ...c, actif: confirm.nextActif } : c
        )
      );

      setSuccess(
        confirm.nextActif
          ? "Collectivité activée"
          : "Collectivité désactivée"
      );

      setTimeout(() => setSuccess(""), 4000);
    } catch {
      setError("Erreur mise à jour.");
    } finally {
      setConfirm(null);
      setActionLoading(false);
    }
  };

  const doDelete = async () => {
    if (!confirm) return;

    setActionLoading(true);

    try {
      const res = await fetch(
        `${API_BASE}/admin/collectivites/${confirm.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (!res.ok) throw new Error();

      setItems((prev) => prev.filter((c) => c.id !== confirm.id));
      setTotal((t) => t - 1);

      setSuccess("Collectivité supprimée");
      setTimeout(() => setSuccess(""), 4000);
    } catch {
      setError("Erreur suppression.");
    } finally {
      setConfirm(null);
      setActionLoading(false);
    }
  };

  return (
    <div className="adminPanel">

      {/* Header */}
      <div className="adminPanelHeader">
        <h2></h2>

        <button
        className="btnSecondary"
        onClick={async () => {
          setPage(1);
          await fetchCollectivites();
          showToast("success", "Liste des collectivités mise à jour");
        }}
        disabled={loading}
      >
        {loading ? "Chargement..." : "Rafraîchir"}
      </button>
      </div>

      {error && <div className="adminError">{error}</div>}
      {success && <div className="adminSuccess">{success}</div>}
      {toast.msg && (
        <div className={`adminToast ${toast.type}`}>
          {toast.msg}
        </div>
      )}

      {/* Table */}
      <div className="adminTableWrap">
        <table className="adminTable">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Ville</th>
              <th>Statut</th>
              <th style={{ width: 200 }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="adminEmpty">
                  Aucune collectivité.
                </td>
              </tr>
            )}

            {items.map((c) => (
              <tr key={c.id}>
              <td style={{ fontSize: '12px' }}>{c.nom}</td>     
              <td style={{ fontSize: '12px' }}>{c.email_contact}</td>
              <td style={{ fontSize: '12px' }}>{c.telephone}</td>
              <td style={{ fontSize: '12px' }}>{c.ville}</td>

                <td>
                  <span className={`statusBadge ${c.actif ? "on" : "off"}`}>
                    {c.actif ? "Actif" : "Inactif"}
                  </span>
                </td>

                <td className="adminActions">
                  <button
                    className={`toggleBtn ${c.actif ? "on" : "off"}`}
                    onClick={() =>
                      setConfirm({
                        type: "toggle",
                        id: c.id,
                        nextActif: !c.actif,
                      })
                    }
                  >
                    {c.actif ? "Désactiver" : "Activer"}
                  </button>

                   {/*
                    <button
                      className="btnDangerOutline"
                      onClick={() =>
                        setConfirm({ type: "delete", id: c.id })
                      }
                    >
                      Supprimer
                    </button>
                   */}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="adminPager">
        <button
          className="btnSecondary"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          ←
        </button>

        <div className="adminPagerInfo">
          Page <b>{page}</b> / {pages} — Total: <b>{total}</b>
        </div>

        <button
          className="btnSecondary"
          onClick={() => setPage((p) => Math.min(pages, p + 1))}
        >
          →
        </button>
      </div>

      {/* Modal */}
      {confirm && (
        <div className="logoutModalOverlay">
          <div className="logoutModal">
            <div className="logoutModalTitle">Confirmation</div>

            <div className="logoutModalText">
              {confirm.type === "toggle"
                ? "Confirmer le changement de statut ?"
                : "Supprimer définitivement cette collectivité ?"}
            </div>

            <div className="logoutModalActions">
              <button
                className="btnSecondary"
                onClick={() => setConfirm(null)}
              >
                Annuler
              </button>

              <button
                className="btnDanger"
                onClick={confirm.type === "toggle" ? doToggle : doDelete}
                disabled={actionLoading}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}